import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle2Icon } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useNotificationsProvider } from '../../context/NotificationsContext';
import { setup2FA, enable2FA, disable2FA, get2FAStatus } from './actions';

TwoFactorAuthDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

/*
 * Two-factor authentication setup dialog. Migrated end-to-end to shadcn:
 * Dialog shell, Alert + Separator chrome, Input for the manual secret
 * and TOTP code, shadcn Button for actions. `QRCodeSVG` (`qrcode.react`)
 * is library-agnostic and unchanged.
 */
export default function TwoFactorAuthDialog({ open, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotificationsProvider();

  const [loading, setLoading] = useState(false);
  // 'check' | 'setup' | 'enabled' — Cognito has no recovery-code lifecycle,
  // so the legacy 'regenerate' / 'show-new-codes' steps are gone.
  const [step, setStep] = useState('check');

  const [is2FAEnabled, setIs2FAEnabled] = useState(false);

  const [otpauthUri, setOtpauthUri] = useState(null);
  const [secret, setSecret] = useState(null);

  const verifySchema = z.object({
    code: z
      .string()
      .regex(/^\d{6}$/, t('2fa.invalid-code', 'Please enter a 6-digit code')),
  });

  const verifyForm = useForm({
    resolver: zodResolver(verifySchema),
    defaultValues: { code: '' },
  });

  const resetState = () => {
    setLoading(false);
    setStep('check');
    setOtpauthUri(null);
    setSecret(null);
    verifyForm.reset({ code: '' });
  };

  const onCloseDialog = () => {
    resetState();
    onClose();
  };

  const check2FAStatus = () => {
    setLoading(true);
    dispatch(get2FAStatus(
      (data) => {
        setIs2FAEnabled(data.enabled);
        setStep(data.enabled ? 'enabled' : 'check');
        setLoading(false);
      },
      () => {
        setIs2FAEnabled(false);
        setStep('check');
        setLoading(false);
      },
    ));
  };

  const handleSetup = () => {
    setLoading(true);
    dispatch(setup2FA(
      (data) => {
        setOtpauthUri(data.otpauthUri);
        setSecret(data.secret);
        setStep('setup');
        setLoading(false);
      },
      () => {
        showNotification('error', t('2fa.setup-error', 'Failed to start 2FA setup. Please try again.'));
        setLoading(false);
      },
    ));
  };

  const handleEnable = verifyForm.handleSubmit((values) => {
    setLoading(true);
    dispatch(enable2FA(
      values.code,
      () => {
        showNotification('success', t('2fa.enabled-success', '2FA enabled successfully'));
        check2FAStatus();
        verifyForm.reset({ code: '' });
      },
      () => {
        showNotification('error', t('2fa.enable-error', 'Invalid code. Please try again.'));
        setLoading(false);
      },
    ));
  });

  const handleDisable = () => {
    setLoading(true);
    dispatch(disable2FA(
      null,
      () => {
        showNotification('success', t('2fa.disabled-success', '2FA disabled successfully'));
        check2FAStatus();
      },
      () => {
        showNotification('error', t('2fa.disable-error', 'Failed to disable 2FA.'));
        setLoading(false);
      },
    ));
  };

  useEffect(() => {
    if (open) check2FAStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const renderCheckStep = () => (
    <div className="space-y-4">
      <p className="text-sm">
        {t('2fa.description', 'Two-factor authentication adds an extra layer of security to your account. You will need to enter a code from your authenticator app when logging in.')}
      </p>
      <Button
        type="button"
        className="w-full"
        onClick={handleSetup}
        disabled={loading}
      >
        {t('2fa.enable-button', 'Enable Two-Factor Authentication')}
      </Button>
    </div>
  );

  const renderSetupStep = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">{t('2fa.scan-qr', 'Step 1: Scan QR Code')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('2fa.scan-description', 'Use Google Authenticator, Microsoft Authenticator, or any TOTP app to scan this QR code:')}
        </p>
      </div>

      {otpauthUri ? (
        <div className="flex justify-center">
          <div className="rounded-md border border-border bg-white p-3">
            <QRCodeSVG value={otpauthUri} size={200} level="M" />
          </div>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <p className="text-sm text-muted-foreground">{t('2fa.manual-entry', 'Or enter this code manually:')}</p>
        <Input value={secret ?? ''} readOnly className="font-mono" />
      </div>

      <Separator />

      <div>
        <h3 className="text-base font-semibold">{t('2fa.verify', 'Step 2: Verify')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('2fa.verify-description', 'Enter the 6-digit code from your authenticator app:')}
        </p>
      </div>
      <Form {...verifyForm}>
        <form onSubmit={handleEnable} className="space-y-3" noValidate>
          <FormField
            control={verifyForm.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    placeholder="000000"
                    maxLength={6}
                    inputMode="numeric"
                    className="text-center font-mono text-2xl tracking-[0.5rem]"
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(e.target.value.replace(/\D/g, ''))
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => {
                resetState();
                check2FAStatus();
              }}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {t('2fa.enable-confirm', 'Enable 2FA')}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );

  const renderEnabledStep = () => (
    <div className="space-y-4">
      <Alert variant="success">
        <CheckCircle2Icon className="size-4" />
        <AlertTitle className="sr-only">{t('2fa.enabled-message', 'Enabled')}</AlertTitle>
        <AlertDescription>
          {t('2fa.enabled-message', 'Two-factor authentication is enabled on your account')}
        </AlertDescription>
      </Alert>
      <p className="text-sm text-muted-foreground">
        {t('2fa.lockout-help', 'Lost access to your authenticator app? Use the "Reset 2FA" link on the login page to receive a recovery email.')}
      </p>
      <Separator />
      <div>
        <h3 className="text-base font-semibold text-destructive">
          {t('2fa.disable-title', 'Disable Two-Factor Authentication')}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('2fa.disable-description-cognito', 'This removes the TOTP requirement from your account. You can re-enable it at any time.')}
        </p>
      </div>
      <Button
        type="button"
        variant="destructive"
        className="w-full"
        onClick={handleDisable}
        disabled={loading}
      >
        {t('2fa.disable-confirm', 'Disable 2FA')}
      </Button>
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          if (loading) return;
          onCloseDialog();
        }
      }}
    >
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-md"
        onInteractOutside={(e) => { if (loading) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (loading) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>{t('2fa.title', 'Two-Factor Authentication')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('2fa.title', 'Two-Factor Authentication')}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          {step === 'check' && !is2FAEnabled ? renderCheckStep() : null}
          {step === 'setup' ? renderSetupStep() : null}
          {step === 'enabled' && is2FAEnabled ? renderEnabledStep() : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
